/**
 * Interview-related constants
 */
/**
 * Maximum number of reschedules allowed per interview (default).
 * This value is used as:
 * - Upper limit when setting recruiter max reschedules
 * - Default value when no custom limit is configured
 * - Fallback when initializing interview reschedule counters
 */
export const INTERVIEW_DEFAULT_MAX_RESCHEDULES = 5;
export const FALLBACK_MEETING_DETAILS = {
    interviewer: 'Recruiter',
    getStartTime: () => new Date()
};
export const MIN_LEAD_TIME_DAYS = 1;
export const MAX_LEAD_TIME_DAYS = 10;
export const DEFAULT_LEAD_TIME_DAYS = 5;
export const MAX_END_DAYS_AHEAD = 30;
/**
 * Default meeting duration (in minutes) when recruiter-specific preference is unavailable.
 */
export const DEFAULT_MEETING_DURATION_MINUTES = 30;
/**
 * Maximum meeting duration (in minutes) accepted by the open time slots endpoint.
 */
export const MAX_OPEN_SLOT_DURATION_MINUTES = 60;
/**
 * Extended property id stored on Outlook events to identify Talent Assistant meetings.
 */
export const INTERVIEW_IID_EXTENDED_PROPERTY_ID = 'String {66f5a359-4659-4830-9070-00047ec6ac6e} Name Iid';
/**
 * Maps each allowed resume MIME type to its file extension.
 * Used to derive both the allowed MIME type set and the accept string for file inputs.
 */
export const RESUME_MIME_TO_EXTENSION = {
    'application/pdf': '.pdf',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/rtf': '.rtf',
    'text/rtf': '.rtf',
    'text/plain': '.txt',
    'application/vnd.oasis.opendocument.text': '.odt'
};
/**
 * Allowed MIME types for resume uploads.
 * Used for validation on both the server (interviewRouter) and the client (ResumeUploadForm).
 */
export const RESUME_ALLOWED_MIME_TYPES = new Set(Object.keys(RESUME_MIME_TO_EXTENSION));
/**
 * Accept string for file inputs, derived from RESUME_MIME_TO_EXTENSION.
 * Automatically stays in sync when new MIME types are added.
 */
export const RESUME_ACCEPT_EXTENSIONS = [...new Set(Object.values(RESUME_MIME_TO_EXTENSION))].join(',');
/**
 * Human-readable list of allowed extensions for error messages (e.g. ".pdf, .doc, or .docx").
 * Derived from RESUME_MIME_TO_EXTENSION so it stays in sync automatically.
 */
const _resumeExtensions = [...new Set(Object.values(RESUME_MIME_TO_EXTENSION))];
export const RESUME_ACCEPT_EXTENSIONS_LABEL = new Intl.ListFormat('en', { type: 'disjunction' }).format(_resumeExtensions);
